import { useState } from "react";
import { Award, Check, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { Aparece } from "./Aparece";
import { Boton } from "./Boton";
import { FUNDADORES, PLAN_ADICIONALES, PLAN_BASE_INCLUYE, PLANES } from "./content";
import { Proximamente } from "./Proximamente";

// Planes con precio público (UF + IVA). Todos terminan en la demo: el producto se vende viéndolo.
// Básico va destacado porque es el tramo con más corredoras. "Recomendado" y no "Más elegido":
// sin clientes todavía, "más elegido" sería un dato inventado.
//
// Las cards se ELIGEN: el resalte sigue al plan que la persona toca (arranca en el recomendado) y la
// elección llega al formulario de demo. "Recomendado" es una etiqueta fija de Básico; el resalte no.
// Es un grupo de radio: una sola elegida, con flechas no, pero Tab + Enter/Espacio sí.

const PlanCard = ({ plan, elegido, onElegir }) => (
  <div
    role="radio"
    aria-checked={elegido}
    tabIndex={0}
    onClick={() => onElegir(plan)}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onElegir(plan);
      }
    }}
    className={cn(
      // `w-full`: el envoltorio de `Aparece` es flex (para igualar altos en la grilla) y sin esto la
      // card se encoge a su contenido — cada plan quedaba de un ancho distinto en mobile.
      "relative flex w-full cursor-pointer flex-col rounded-2xl bg-white p-6 outline-none transition-shadow duration-200 focus-visible:ring-4 focus-visible:ring-indigo-200",
      elegido
        ? "shadow-md ring-2 ring-indigo-500"
        : "shadow-sm ring-1 ring-inset ring-slate-200/60 hover:shadow-md hover:ring-indigo-200",
    )}
    data-testid={`plan-${plan.key}`}
  >
    {plan.destacado && (
      <span className="absolute -top-3 left-6 rounded-full bg-indigo-600 px-3 py-1 text-xs font-semibold text-white">
        Recomendado
      </span>
    )}
    <h3 className="font-display text-lg font-extrabold text-slate-900">{plan.nombre}</h3>
    <p className="mt-1 min-h-[2.5rem] text-sm text-slate-500">{plan.para}</p>
    <p className="mt-5 flex items-baseline gap-1.5">
      <span className="font-display text-4xl font-extrabold tabular-nums tracking-tight text-slate-900">{plan.uf}</span>
      <span className="text-base font-bold text-slate-900">UF</span>
      <span className="text-sm text-slate-500">+ IVA / mes</span>
    </p>
    <div className="mt-5 space-y-1 rounded-xl bg-slate-50/80 px-4 py-3 ring-1 ring-inset ring-slate-200/60">
      <p className="text-sm font-semibold text-slate-800">{plan.propiedades}</p>
      <p className="text-sm text-slate-600">{plan.usuarios}</p>
    </div>
    <ul className="mt-5 flex-1 space-y-2.5">
      {plan.extras.map((e) => (
        <li key={e} className="flex gap-2 text-sm text-slate-600">
          <Check className="mt-0.5 h-4 w-4 shrink-0 text-indigo-500" /> {e}
        </li>
      ))}
    </ul>
    <Boton
      href="#demo"
      variante={elegido ? "primario" : "secundario"}
      className="mt-6 w-full"
      onClick={() => onElegir(plan)}
      data-testid={`plan-${plan.key}-demo`}
    >
      Agenda una demo
    </Boton>
  </div>
);

export const Planes = ({ onElegirPlan }) => {
  const [elegido, setElegido] = useState(() => (PLANES.find((p) => p.destacado) || PLANES[0]).key);
  const elegir = (plan) => {
    setElegido(plan.key);
    onElegirPlan(plan.nombre);
  };
  return (
  <section id="planes" className="scroll-mt-16 bg-white" data-testid="landing-planes">
    <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:py-28">
      <Aparece className="max-w-2xl">
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">Planes</p>
        <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
          Un plan para cada cartera. <span className="text-slate-400">Crece sin cambiar de sistema.</span>
        </h2>
      </Aparece>

      <Aparece className="mt-10 flex items-start gap-4 rounded-2xl border-l-4 border-l-gold-600 bg-white p-5 shadow-sm ring-1 ring-inset ring-slate-200/60" data-testid="planes-fundadores">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-indigo-900">
          <Award className="h-5 w-5 text-gold-500" />
        </span>
        <div>
          <p className="text-base font-bold text-slate-800">{FUNDADORES.titulo}</p>
          <p className="mt-1 text-sm leading-relaxed text-slate-600">{FUNDADORES.texto}</p>
        </div>
      </Aparece>

      <div role="radiogroup" aria-label="Planes" className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {PLANES.map((p, i) => (
          <Aparece key={p.key} delay={i * 0.05} className="flex">
            <PlanCard plan={p} elegido={elegido === p.key} onElegir={elegir} />
          </Aparece>
        ))}
      </div>

      <div className="mt-10 grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <Aparece className="rounded-2xl bg-slate-50/80 p-6 ring-1 ring-inset ring-slate-200/60">
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">Incluido en todos los planes</p>
          <ul className="mt-4 grid gap-x-6 gap-y-2.5 sm:grid-cols-2">
            {PLAN_BASE_INCLUYE.map((b) => (
              <li key={b.label} className="flex gap-2 text-sm text-slate-700">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                <span>
                  {b.label}
                  {b.proximamente && <Proximamente className="ml-2 align-middle" />}
                </span>
              </li>
            ))}
          </ul>
        </Aparece>
        <Aparece className="rounded-2xl bg-slate-50/80 p-6 ring-1 ring-inset ring-slate-200/60">
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">Adicionales</p>
          <ul className="mt-4 space-y-4">
            {PLAN_ADICIONALES.map((a) => (
              <li key={a.label} className="flex items-start justify-between gap-4">
                <div className="flex gap-2">
                  <Plus className="mt-0.5 h-4 w-4 shrink-0 text-indigo-500" />
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{a.label}</p>
                    <p className="text-xs text-slate-500">{a.nota}</p>
                  </div>
                </div>
                <p className="whitespace-nowrap text-sm font-bold tabular-nums text-slate-900">
                  {a.precio} <span className="font-normal text-slate-500">+ IVA / mes</span>
                </p>
              </li>
            ))}
          </ul>
        </Aparece>
      </div>
      <p className="mt-6 text-xs text-slate-500">
        Precios mensuales en UF más IVA. Próximamente: publicación en TocToc e iCasas, y conciliación bancaria automática.
      </p>
    </div>
  </section>
  );
};
